interface RecentActivityItem {
  label: string;
  distance: number;
  goalMet: boolean;
}

interface YourChallengeStatsProps {
  userAvatar: string;
  totalDistance: number;
  distanceGoal: number;
  averagePace: string | null;
  bestDay: { date: string; distance: number } | null;
  activeDays: number;
  totalActiveDays: number;
  recentActivities: RecentActivityItem[];
}

export function YourChallengeStats({
  userAvatar,
  totalDistance,
  distanceGoal,
  averagePace,
  bestDay,
  activeDays,
  totalActiveDays,
  recentActivities,
}: YourChallengeStatsProps) {
  const distanceProgress = Math.min((totalDistance / distanceGoal) * 100, 100);

  // Derive status from goalMet boolean
  const getActivityStatus = (
    goalMet: boolean,
    label: string
  ): "completed" | "pending" | "missed" => {
    if (label === "Today") return goalMet ? "completed" : "pending";
    return goalMet ? "completed" : "missed";
  };

  return (
    <div className="px-4 py-2">
      <div className="rounded-4xl bg-gradient-to-br from-[#16a34a]/20 via-[#16a34a]/10 to-transparent p-4 border border-[#16a34a]/40">
        {/* Header */}
        <div className="flex items-center gap-3">
          <img
            className="size-12 rounded-full object-cover border-2 border-[#16a34a]/80"
            src={userAvatar}
            alt="Your profile"
          />
          <div>
            <p className="font-bold text-xl text-content-light dark:text-content-dark">
              Your Challenge Stats
            </p>
            <p className="text-sm text-content-light/70 dark:text-content-dark/70 -mt-0.5">
              Your performance overview
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          {/* Total Distance - Full Width */}
          <div className="flex flex-col gap-2 rounded-4xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 p-4 col-span-2">
            <div className="flex items-center gap-2 text-sm text-content-light/70 dark:text-content-dark/70">
              <span className="material-symbols-outlined !text-base">
                route
              </span>
              <span>Total Distance</span>
            </div>
            <div className="mt-1">
              <div className="flex justify-between items-baseline">
                <span className="text-2xl font-bold text-content-light dark:text-content-dark">
                  {totalDistance} km
                </span>
                <span className="text-sm font-medium text-content-light/60 dark:text-content-dark/60">
                  / {distanceGoal}km
                </span>
              </div>
              <div className="w-full bg-primary/10 rounded-full h-2 mt-2">
                <div
                  className="bg-primary h-2 rounded-full"
                  style={{
                    width: `${distanceProgress}%`,
                    filter: "drop-shadow(0 0 5px rgba(13, 242, 13, 0.6))",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Average Pace */}
          <div className="flex flex-col rounded-4xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 p-4">
            <div className="flex items-center gap-2 text-sm text-content-light/70 dark:text-content-dark/70">
              <span className="material-symbols-outlined !text-base">
                speed
              </span>
              <span>Average Pace</span>
            </div>
            <div className="relative mt-2 flex flex-col justify-end h-24">
              {/* Mini Chart */}
              <div className="absolute inset-0">
                <svg
                  className="w-full h-full"
                  fill="none"
                  preserveAspectRatio="none"
                  viewBox="0 0 100 40"
                >
                  <defs>
                    <linearGradient
                      id="pace-gradient"
                      x1="0"
                      x2="0"
                      y1="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#0df20d" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#0df20d" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path
                    className="stroke-primary"
                    d="M0 30 C 20 35, 40 20, 60 15, 80 10, 90 5, 100 5"
                    strokeWidth="2"
                    style={{
                      filter: "drop-shadow(0 0 5px rgba(13, 242, 13, 0.6))",
                    }}
                  />
                  <path
                    d="M0 40 L0 30 C 20 35, 40 20, 60 15, 80 10, 90 5, 100 5 L 100 40 Z"
                    fill="url(#pace-gradient)"
                  />
                </svg>
              </div>
              <div className="relative z-10">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-extrabold text-content-light dark:text-content-dark">
                    {averagePace ?? "--"}
                  </span>
                  <span className="text-base font-medium text-content-light/70 dark:text-content-dark/70">
                    /km
                  </span>
                </div>
                {averagePace && (
                  <div className="flex items-center gap-1 text-primary/90 text-sm font-medium">
                    <span
                      className="material-symbols-outlined !text-base !leading-none"
                      style={{ fontVariationSettings: "'wght' 300" }}
                    >
                      trending_up
                    </span>
                    <span>Improving</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Best Day + Active Days (Stacked) */}
          <div className="flex flex-col gap-3">
            {/* Best Day */}
            <div className="flex flex-col gap-1 rounded-4xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 p-3 flex-1">
              <div className="flex items-center gap-1.5 text-sm text-content-light/70 dark:text-content-dark/70">
                <span className="material-symbols-outlined !text-sm">
                  emoji_events
                </span>
                <span>Best Day</span>
              </div>
              <span className="text-2xl font-bold text-content-light dark:text-content-dark">
                {bestDay?.distance ?? "--"}
                <span className="text-lg font-medium text-content-light/70 dark:text-content-dark/70 ml-1">
                  km
                </span>
              </span>
            </div>

            {/* Active Days */}
            <div className="flex flex-col gap-1 rounded-4xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 p-3 flex-1">
              <div className="flex items-center gap-1.5 text-sm text-content-light/70 dark:text-content-dark/70">
                <span className="material-symbols-outlined !text-sm">
                  event_repeat
                </span>
                <span>Active Days</span>
              </div>
              <span className="text-2xl font-bold text-content-light dark:text-content-dark">
                {activeDays}/{totalActiveDays}
              </span>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mt-4 rounded-4xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 p-4">
          <div className="flex items-center justify-between">
            <p className="text-base font-bold text-content-light dark:text-content-dark mb-2">
              Recent Activity
            </p>
          </div>
          <div className="flex flex-col gap-2">
            {recentActivities.map((activity, index) => {
              const status = getActivityStatus(
                activity.goalMet,
                activity.label
              );
              return (
                <div
                  key={index}
                  className={`flex items-center justify-between text-sm py-1.5 ${
                    index < recentActivities.length - 1
                      ? "border-b border-black/5 dark:border-white/5"
                      : ""
                  }`}
                >
                  <span className="text-content-light/80 dark:text-content-dark/80">
                    {activity.label}
                    <span className="text-content-light/50 dark:text-content-dark/50 ml-2">
                      · {activity.distance} km
                    </span>
                  </span>
                  <div
                    className={`flex items-center gap-1.5 font-medium ${
                      status === "completed"
                        ? "text-primary"
                        : status === "pending"
                        ? "text-orange-400"
                        : "text-red-400"
                    }`}
                  >
                    <span className="material-symbols-outlined !text-base">
                      {status === "completed"
                        ? "task_alt"
                        : status === "pending"
                        ? "schedule"
                        : "cancel"}
                    </span>
                    <span className="capitalize">{status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
